var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { useBlockContext, useEditor } from "./BlockEngineApi.js";
import { createElement, useContext, useMemo, forwardRef, } from "react";
import { BlockContext } from "./BlockViewer.js";
import { addBlockRegistryListener, getBlock, hasBlock, removeBlockRegistryListener, } from "./registerBlock.js";
import { ChildrenBlockContext, FrontendChildrenBlock } from "./ChildrenBlock.js";
export const outOfScope = (data) => {
    console.error("[Smartfire Engine] You tried to set data inside a non editor environment", data);
};
export class BlockEngineApiFrontend {
    constructor() {
        this.loadedBlocks = {};
        this.loadedBlocksPromises = {};
        this.loadedBlocksErrors = {};
    }
    useEditor() {
        return {
            isEditing: false,
            updateData: outOfScope,
        };
    }
    useInspectorControl(_rendering, _deps = []) {
        /* NOOP */
    }
    useToolbar(_rendering, _deps = []) {
        /* NOOP */
    }
    useSmartProp(name) {
        var _a;
        const blockContext = useBlockContext();
        const editor = useEditor();
        const valid = useMemo(() => {
            let currentBlock = blockContext === null || blockContext === void 0 ? void 0 : blockContext.Block;
            return currentBlock === null || currentBlock === void 0 ? void 0 : currentBlock.smartProps.hasOwnProperty(name);
        }, [blockContext === null || blockContext === void 0 ? void 0 : blockContext.Block]);
        const update = useMemo(() => {
            if (!editor.isEditing) {
                return outOfScope;
            }
            return (value) => editor.updateData({ [name]: value });
        }, [editor.isEditing, editor.updateData, name]);
        if (!valid) {
            throw new Error(`You are using a non existent smartProps "${name}" on the block ${blockContext === null || blockContext === void 0 ? void 0 : blockContext.Block.name}.
You can register it by adding it inside the defineBlock declaration. (Existing smartProps are ${JSON.stringify(Object.keys((_a = blockContext === null || blockContext === void 0 ? void 0 : blockContext.Block.smartProps) !== null && _a !== void 0 ? _a : {}))})`);
        }
        return [blockContext === null || blockContext === void 0 ? void 0 : blockContext.attributes[name], update];
    }
    useChildrenBlocks() {
        const blockContext = useContext(BlockContext);
        const children = blockContext === null || blockContext === void 0 ? void 0 : blockContext.children;
        const Child = useMemo(() => {
            var _a;
            let child = forwardRef(function Child(_a, ref) {
                var { index, child, elementType } = _a, props = __rest(_a, ["index", "child", "elementType"]);
                const childrenContext = useMemo(() => ({
                    parentBlock: blockContext,
                    unique: true,
                    childrenProps: props,
                    forwardedChildrenRef: ref,
                }), [blockContext, props]);
                const ChildValue = child !== null && child !== void 0 ? child : children === null || children === void 0 ? void 0 : children[index];
                if (!ChildValue) {
                    throw new Error("No child available on index " + index);
                }
                return createElement(ChildrenBlockContext.Provider, {
                    value: childrenContext,
                }, ChildValue);
            });
            Object.defineProperty(child, "name", {
                value: "BlockChild-" + ((_a = blockContext === null || blockContext === void 0 ? void 0 : blockContext.Block.name) !== null && _a !== void 0 ? _a : "Raw"),
            });
            return child;
        }, [children]);
        const Children = FrontendChildrenBlock;
        return {
            Child,
            Children,
            children,
        };
    }
    useBlockContext() {
        const blockContext = useContext(BlockContext);
        if (!blockContext) {
            throw new Error("No block context available");
        }
        return blockContext;
    }
    useBlock(name) {
        if (this.loadedBlocks[name]) {
            if (this.loadedBlocks[name] !== null) {
                return this.loadedBlocks[name];
            }
            else {
                throw this.loadedBlocksErrors[name];
            }
        }
        if (this.loadedBlocksPromises[name]) {
            throw this.loadedBlocksPromises[name];
        }
        this.loadedBlocksPromises[name] = new Promise((resolve, reject) => {
            const load = () => getBlock(name).then(resolve, reject);
            if (hasBlock(name)) {
                load();
            }
            else {
                const listener = (e) => {
                    if (e.detail.name === name) {
                        load();
                        removeBlockRegistryListener("registration", listener);
                    }
                };
                addBlockRegistryListener("registration", listener);
            }
        }).then((result) => {
            this.loadedBlocks[name] = result;
            return result;
        }, (error) => {
            this.loadedBlocks[name] = null;
            this.loadedBlocksErrors[name] = error;
            throw error;
        });
        throw this.loadedBlocksPromises[name];
    }
}

//# sourceMappingURL=BlockEngineApiFrontend.js.map
